import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, FileText, Check, AlertCircle, Loader2, Shield, Clock, CheckCircle, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { generatePartialPreviewHtml, formatDateFr } from '../utils/previewUtils';

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/3cI28q4NOcW936FcvOgjC04";

export default function DeclarationSituationSeoPage() {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    lieu_naissance: '',
    adresse: '',
    situation: '',
    lieu: '',
    date_document: new Date().toISOString().split('T')[0]
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const validateForm = () => {
    return formData.nom && formData.prenom && formData.adresse && 
           formData.situation && formData.lieu && formData.date_document;
  };

  const generatePreviewHtml = () => {
    const { nom, prenom, adresse, situation, lieu, date_document, date_naissance, lieu_naissance } = formData;
    
    let naissanceText = '';
    if (date_naissance) {
      const dn = new Date(date_naissance);
      naissanceText = `, né(e) le ${dn.getDate()}/${dn.getMonth()+1}/${dn.getFullYear()}`;
      if (lieu_naissance) {
        naissanceText += ` à ${lieu_naissance}`;
      }
    }
    
    const introText = `Je soussigné(e) ${prenom} ${nom.toUpperCase()}${naissanceText}, demeurant à ${adresse.replace(/\n/g, ', ')}, déclare sur l'honneur être dans la situation suivante :`;
    
    return generatePartialPreviewHtml({
      nom,
      prenom,
      adresse,
      dateStr: formatDateFr(date_document),
      lieu,
      title: "DÉCLARATION DE SITUATION PERSONNELLE",
      content: situation,
      introText
    });
  };
  
  const handleGeneratePreview = () => {
    if (!validateForm()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setPreviewLoading(true);
    setError(null);
    
    setTimeout(() => {
      const html = generatePreviewHtml();
      setPreviewHtml(html);
      setCurrentStep(2);
      setPreviewLoading(false);
    }, 500);
  };
  
  const handlePayment = () => {
    setPaymentLoading(true);
    
    const paymentData = {
      documentId: 'declaration-situation',
      documentName: 'Déclaration de situation personnelle',
      formData: formData,
      previewHtml: previewHtml,
      timestamp: Date.now()
    };
    localStorage.setItem('pdf_facile_payment', JSON.stringify(paymentData));
    
    window.location.href = STRIPE_PAYMENT_LINK;
  };
  
  return (
    <>
      <Helmet>
        <title>Déclaration de situation personnelle PDF à télécharger | PDF Facile</title>
        <meta name="description" content="Créez et téléchargez votre déclaration de situation personnelle en PDF. Document conforme pour déclarer votre situation familiale, professionnelle ou personnelle. Aperçu gratuit, téléchargement immédiat." />
        <link rel="canonical" href="https://www.pdf-facile.fr/declaration-situation-personnelle-pdf" />
      </Helmet>
      
      <div className="min-h-screen flex flex-col bg-white">
        <Header showBack backTo="/" />
        
        <main className="flex-1">
          {/* Hero Section */}
          <section className="bg-slate-50 py-8 md:py-12">
            <div className="container-app max-w-3xl">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Link to="/" className="hover:text-primary">Accueil</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-foreground font-medium">Déclaration de situation personnelle PDF</span>
              </nav>
              
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-4" data-testid="seo-h1">
                Déclaration de situation personnelle PDF à télécharger
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                La déclaration de situation personnelle est un document officiel permettant de déclarer votre 
                situation familiale, professionnelle ou personnelle auprès d'une administration ou d'un organisme. 
                Ce document est souvent demandé pour les démarches sociales, fiscales ou administratives en France.
              </p>
              
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>Document conforme</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>Prêt en 2 minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Sans inscription</span>
                </div>
              </div>
            </div>
          </section>
          
          {/* Step Indicator */}
          <section className="py-4 border-b border-slate-200">
            <div className="container-app max-w-3xl">
              <div className="flex items-center justify-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <div className={`step-dot ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : 'inactive'}`}>
                    {currentStep > 1 ? <Check className="w-4 h-4" /> : '1'}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">Formulaire</span>
                </div>
                <div className={`step-line flex-1 max-w-[60px] ${currentStep > 1 ? 'completed' : ''}`} />
                <div className="flex items-center gap-2">
                  <div className={`step-dot ${currentStep >= 2 ? 'active' : 'inactive'}`}>
                    2
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">Aperçu & Téléchargement</span>
                </div>
              </div>
            </div>
          </section>
          
          {/* Form / Preview Section */}
          <section className="section-spacing">
            <div className="container-app max-w-2xl">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              
              {currentStep === 1 && (
                <div data-testid="form-step">
                  <h2 className="text-lg font-semibold mb-6">Remplissez votre déclaration de situation</h2>
                  
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-field">
                        <Label htmlFor="nom" className="label-field">
                          Nom <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="nom"
                          type="text"
                          placeholder="Dupont"
                          value={formData.nom}
                          onChange={(e) => handleInputChange('nom', e.target.value)}
                          className="input-field"
                        />
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="prenom" className="label-field">
                          Prénom <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="prenom"
                          type="text"
                          placeholder="Jean"
                          value={formData.prenom}
                          onChange={(e) => handleInputChange('prenom', e.target.value)}
                          className="input-field"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-field">
                        <Label htmlFor="date_naissance" className="label-field">
                          Date de naissance <span className="text-slate-400 text-xs">(optionnel)</span>
                        </Label>
                        <Input
                          id="date_naissance"
                          type="date"
                          value={formData.date_naissance}
                          onChange={(e) => handleInputChange('date_naissance', e.target.value)}
                          className="input-field"
                        />
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="lieu_naissance" className="label-field">
                          Lieu de naissance <span className="text-slate-400 text-xs">(optionnel)</span>
                        </Label>
                        <Input
                          id="lieu_naissance"
                          type="text"
                          placeholder="Paris"
                          value={formData.lieu_naissance}
                          onChange={(e) => handleInputChange('lieu_naissance', e.target.value)}
                          className="input-field"
                        />
                      </div>
                    </div>
                    
                    <div className="form-field">
                      <Label htmlFor="adresse" className="label-field">
                        Adresse complète <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="adresse"
                        placeholder="123 rue de la Paix, 75001 Paris"
                        value={formData.adresse}
                        onChange={(e) => handleInputChange('adresse', e.target.value)}
                        className="input-field min-h-[80px] py-3"
                      />
                    </div>
                    
                    <div className="form-field">
                      <Label htmlFor="situation" className="label-field">
                        Description de votre situation <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="situation"
                        placeholder="Décrivez votre situation personnelle, familiale ou professionnelle..."
                        value={formData.situation}
                        onChange={(e) => handleInputChange('situation', e.target.value)}
                        className="input-field min-h-[150px] py-3"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Exemples : situation matrimoniale (célibataire, marié, pacsé), situation professionnelle 
                        (salarié, demandeur d'emploi, étudiant), situation de logement, etc.
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-field">
                        <Label htmlFor="lieu" className="label-field">
                          Fait à (lieu) <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="lieu"
                          type="text"
                          placeholder="Paris"
                          value={formData.lieu}
                          onChange={(e) => handleInputChange('lieu', e.target.value)}
                          className="input-field"
                        />
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="date_document" className="label-field">
                          Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="date_document"
                          type="date"
                          value={formData.date_document}
                          onChange={(e) => handleInputChange('date_document', e.target.value)}
                          className="input-field"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <Button
                      onClick={handleGeneratePreview}
                      disabled={!validateForm() || previewLoading}
                      className="btn-primary w-full"
                    >
                      {previewLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Génération en cours...
                        </>
                      ) : (
                        <>
                          <FileText className="w-5 h-5 mr-2" />
                          Générer l'aperçu gratuitement
                        </>
                      )}
                    </Button>
                    
                    {/* Réassurance sous le bouton */}
                    <div className="mt-6 text-xs text-slate-500 space-y-1">
                      <p>• Documents conformes aux usages administratifs français</p>
                      <p>• Aperçu partiel gratuit avant paiement</p>
                      <p>• Téléchargement immédiat après paiement</p>
                      <p>• Aucune inscription requise</p>
                    </div>
                  </div>
                </div>
              )}
              
              {currentStep === 2 && previewHtml && (
                <div data-testid="preview-step">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold">Aperçu de votre déclaration</h2>
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="text-sm text-accent hover:underline"
                    >
                      Modifier
                    </button>
                  </div>
                  
                  <div 
                    className="preview-document mb-6"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                  
                  <div className="bg-slate-50 rounded-lg p-6 text-center">
                    <div className="payment-badge inline-flex mb-4">
                      <Lock className="w-4 h-4" />
                      <span>Paiement sécurisé</span>
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">
                      Votre déclaration de situation est prête
                    </h3>
                    <p className="text-muted-foreground text-sm mb-6">
                      Le PDF sera identique à l'aperçu ci-dessus, au format A4.
                    </p>
                    
                    <Button
                      onClick={handlePayment}
                      disabled={paymentLoading}
                      className="btn-primary w-full"
                    >
                      {paymentLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Redirection...
                        </>
                      ) : (
                        <>
                          <Lock className="w-5 h-5 mr-2" />
                          Télécharger le PDF – 4,99 €
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-muted-foreground mt-4">
                      Paiement unique • Sans abonnement • Téléchargement immédiat
                    </p>
                    
                    <p className="text-xs text-slate-400 mt-3">
                      Paiement sécurisé via Stripe – transactions protégées par chiffrement SSL
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
          
          {/* SEO Content Section */}
          <section className="bg-slate-50 section-spacing">
            <div className="container-app max-w-3xl">
              <div className="prose prose-slate max-w-none">
                <h2 className="text-xl font-semibold mb-4">À quoi sert une déclaration de situation personnelle ?</h2>
                <p className="text-muted-foreground mb-6">
                  La déclaration de situation personnelle est un document officiel permettant de déclarer 
                  formellement votre situation actuelle (familiale, professionnelle, financière ou personnelle) 
                  auprès d'une administration, d'un organisme ou d'une entreprise. Contrairement à un simple 
                  justificatif, ce document repose sur votre déclaration personnelle et engage votre responsabilité.
                </p>
                
                <h2 className="text-xl font-semibold mb-4">Dans quels cas utiliser une déclaration de situation personnelle ?</h2>
                <p className="text-muted-foreground mb-4">
                  Ce document est couramment demandé dans les situations suivantes :
                </p>
                <ul className="text-muted-foreground mb-6 space-y-2">
                  <li><strong>Situation familiale :</strong> déclaration de vie maritale, de concubinage, de séparation, de veuvage</li>
                  <li><strong>Situation professionnelle :</strong> déclaration de recherche d'emploi, de travail indépendant, de congé parental</li>
                  <li><strong>Démarches CAF :</strong> déclaration de situation pour les allocations familiales, APL, RSA</li>
                  <li><strong>Démarches fiscales :</strong> déclaration de changement de situation pour les impôts</li>
                  <li><strong>Demande de logement :</strong> déclaration de situation pour un dossier de location ou de logement social</li>
                  <li><strong>Démarches bancaires :</strong> déclaration de situation financière pour un crédit ou une ouverture de compte</li>
                  <li><strong>Organismes de retraite :</strong> déclaration de situation pour les caisses de retraite</li>
                </ul>
                
                <h2 className="text-xl font-semibold mb-4">Comment obtenir votre déclaration de situation en PDF ?</h2>
                <p className="text-muted-foreground mb-4">
                  Sur PDF Facile, la création de votre déclaration de situation personnelle est simple et rapide :
                </p>
                <ol className="text-muted-foreground mb-6 space-y-2">
                  <li><strong>1. Remplissez vos informations</strong> : état civil et description détaillée de votre situation.</li>
                  <li><strong>2. Vérifiez l'aperçu partiel</strong> : visualisez la mise en forme et vos informations avant de payer.</li>
                  <li><strong>3. Téléchargez le PDF</strong> : après paiement sécurisé de 4,99 €, votre document est prêt à imprimer et à signer.</li>
                </ol>
                <p className="text-muted-foreground mb-6">
                  Aucune inscription requise. Votre déclaration est générée instantanément au format PDF A4, 
                  conforme aux usages administratifs français.
                </p>
                
                <h2 className="text-xl font-semibold mb-4">Valeur légale et responsabilités</h2>
                <p className="text-muted-foreground mb-4">
                  La déclaration de situation personnelle générée par PDF Facile est conforme aux attentes 
                  des administrations françaises. Elle constitue une déclaration sur l'honneur et peut être 
                  utilisée dans la plupart des démarches administratives.
                </p>
                <p className="text-muted-foreground mb-6">
                  <strong>Attention :</strong> En signant ce document, vous certifiez l'exactitude des informations 
                  déclarées. Toute fausse déclaration est susceptible d'entraîner des sanctions pénales 
                  conformément à l'article 441-7 du Code pénal. Certains organismes peuvent demander des 
                  justificatifs complémentaires pour vérifier votre situation.
                </p>
              </div>
              
              <div className="mt-8 text-center">
                <Button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="btn-primary"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Créer ma déclaration de situation
                </Button>
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
}
